import { EMPTY } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpRequest, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { HttpInterceptor } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AuthToken } from 'src/app/modules/core/store/models/auth-token.model';
import * as fromAuth from '../store/auth';
import { AuthApiService } from '../store/auth/auth-api.service';

@Injectable()
export class AuthHttpInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<AuthToken | null>(null);

  constructor(
    private store: Store<any>,
    private http: HttpClient,
    private authApiService: AuthApiService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let token: AuthToken | null = null;

    this.store.select(fromAuth.selectToken).pipe(take(1)).subscribe(t => (token = t));

    let request = req;
    if (token) {
      request = this.addTokenHeader(req, token);
    }

    return next.handle(request).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401) {

          // 1. SESSION EXPIRED ON SERVER → logout immediately
          if (error.headers?.get('X-Session-Expired') === 'true' || error.error?.message == 'X-Session-Expired') {
            console.log('Session expired — logging out');
            this.store.dispatch(fromAuth.logoutSuccess());
            return EMPTY;
          }

          //  2. TOKEN EXPIRED → try refresh (but not for refresh endpoint itself)
          if (!req.url.includes('iam/auth/authenticated-token') && token) {
            console.log('token expired — try refresh');
            return this.handle401Error(request, next);
          }
        }

        console.error(error);
        return throwError(() => error);
      })
    );
  }

  private addTokenHeader(request: HttpRequest<any>, token: AuthToken) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token.access_token}`
      }
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authApiService.refreshAccessToken().pipe(
        take(1),
        switchMap(newToken => {
          this.isRefreshing = false;

          if (!newToken) {
            this.store.dispatch(fromAuth.logout());
            return EMPTY;
          }

          this.store.dispatch(fromAuth.setToken({ token: newToken }));
          this.refreshTokenSubject.next(newToken);

          return next.handle(this.addTokenHeader(request, newToken));
        }),
        catchError(err => {
          this.isRefreshing = false;
          this.store.dispatch(fromAuth.logout());
          return EMPTY;
        })
      );
    }

    // Wait until refresh completes
    return this.refreshTokenSubject.pipe(
      filter(t => t !== null),
      take(1),
      switchMap(t => next.handle(this.addTokenHeader(request, t!)))
    );
  }
}
